// ─── Encuadre de un clip dentro del cuadro del video ────────────────────────
// Matemática pura del encuadre manual: escalar, mover, recortar y rotar un
// clip de video desde el preview del Estudio de Video.
//
// El clip se dibuja SIEMPRE ocupando el cuadro entero (`object-fit: cover`) y
// el encuadre se aplica encima con dos cosas de CSS:
//
//   • `clip-path: inset(...)`  → el RECORTE. Tapa un porcentaje de cada lado
//     sin mover ni deformar la imagen: lo que queda visible es un rectángulo
//     más chico, y por los costados se ve el fondo. Un recorte NO reescala el
//     contenido (eso lo hace `scale`), que es justamente lo que distingue
//     "recortar" de "achicar".
//   • `transform: translate() rotate() scale()` → posición, giro y tamaño.
//
// El orden importa y es el de esa línea: el recorte define QUÉ se ve (en las
// coordenadas del clip, sin transformar), y recién después el transform mueve,
// gira y escala ese recorte. Al revés, recortar un clip ya rotado cortaría en
// diagonal respecto de la imagen y sería imposible de razonar.
//
// ⚠️ Este archivo tiene un ESPEJO en `remotion/src/encuadre.ts`, que es el que
// usa el render del MP4. Los dos tienen que decir exactamente lo mismo o el
// export va a salir distinto de lo que se ve en el editor. No hay import entre
// paquetes (Remotion es un proyecto Node aparte), así que se mantienen a mano
// — igual que `types.ts` / `remotion/src/types.ts`.

export interface RecorteDeClip {
  /** Porcentaje del alto recortado desde arriba (0..100). */
  top: number;
  /** Porcentaje del ancho recortado desde la derecha (0..100). */
  right: number;
  /** Porcentaje del alto recortado desde abajo (0..100). */
  bottom: number;
  /** Porcentaje del ancho recortado desde la izquierda (0..100). */
  left: number;
}

export interface EncuadreDeClip {
  /** 1 = el clip llena el cuadro. 2 = al doble de tamaño. */
  scale?: number;
  /** Centro del clip como % del cuadro. 50 = centrado. */
  xPct?: number;
  yPct?: number;
  /** Giro en grados, sentido horario. */
  rotation?: number;
  crop?: Partial<RecorteDeClip>;
}

export interface EncuadreNormalizado {
  scale: number;
  xPct: number;
  yPct: number;
  rotation: number;
  crop: RecorteDeClip;
}

export const ESCALA_MIN = 0.1;
export const ESCALA_MAX = 5;
/** Suma máxima de recorte por eje. Deja siempre una franja visible: con 100
 *  el clip desaparecería y no habría de dónde agarrarlo para traerlo de vuelta. */
export const RECORTE_MAX_POR_EJE = 90;

export const SIN_RECORTE: RecorteDeClip = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export const ENCUADRE_NEUTRO: EncuadreNormalizado = {
  scale: 1,
  xPct: 50,
  yPct: 50,
  rotation: 0,
  crop: SIN_RECORTE,
};

const clamp = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, v));

/** Un número usable, o el default si viene `undefined`/`NaN` (proyectos viejos
 *  guardados antes de que existiera el encuadre caen acá). */
const num = (v: number | undefined, porDefecto: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : porDefecto;

/** Rellena los defaults y acota todo a rangos usables. Es la única puerta de
 *  entrada: nada más en el módulo asume que los valores vienen sanos. */
export function normalizarEncuadre(e?: EncuadreDeClip | null): EncuadreNormalizado {
  if (!e) return ENCUADRE_NEUTRO;

  const crop = e.crop ?? {};
  let top = clamp(num(crop.top, 0), 0, RECORTE_MAX_POR_EJE);
  let bottom = clamp(num(crop.bottom, 0), 0, RECORTE_MAX_POR_EJE);
  let left = clamp(num(crop.left, 0), 0, RECORTE_MAX_POR_EJE);
  let right = clamp(num(crop.right, 0), 0, RECORTE_MAX_POR_EJE);

  // Si los dos lados de un eje suman de más, se recorta el exceso del segundo.
  // Pasa al cargar un proyecto con datos corruptos, no por gesto del usuario
  // (los gestos ya clampean contra el lado opuesto).
  if (top + bottom > RECORTE_MAX_POR_EJE) {
    bottom = Math.max(0, RECORTE_MAX_POR_EJE - top);
  }
  if (left + right > RECORTE_MAX_POR_EJE) {
    right = Math.max(0, RECORTE_MAX_POR_EJE - left);
  }

  return {
    scale: clamp(num(e.scale, 1), ESCALA_MIN, ESCALA_MAX),
    xPct: clamp(num(e.xPct, 50), -100, 200),
    yPct: clamp(num(e.yPct, 50), -100, 200),
    rotation: num(e.rotation, 0),
    crop: { top, right, bottom, left },
  };
}

/** true si el clip está sin tocar (llena el cuadro, centrado, sin girar ni
 *  recortar). Sirve para no escribir CSS ni campos de más cuando no hace falta,
 *  y para saber si mostrar el botón de "restablecer". */
export function esEncuadreNeutro(e?: EncuadreDeClip | null): boolean {
  const n = normalizarEncuadre(e);
  return (
    n.scale === 1 &&
    n.xPct === 50 &&
    n.yPct === 50 &&
    n.rotation === 0 &&
    n.crop.top === 0 &&
    n.crop.right === 0 &&
    n.crop.bottom === 0 &&
    n.crop.left === 0
  );
}

export interface EstiloDeEncuadre {
  clipPath?: string;
  transform?: string;
  transformOrigin: string;
}

/** Qué fracción de su caja ocupa el contenido en cada eje.
 *
 *  Un video llena la suya (`object-cover`): 1 y 1. Una imagen se dibuja con
 *  `contain`, así que llena un eje y en el otro queda centrada con aire a los
 *  costados. */
function fraccionesDelContenido(
  aspecto: number | undefined,
  aspectoDeLaCaja: number,
): { fx: number; fy: number } {
  if (!aspecto || !isFinite(aspectoDeLaCaja) || aspectoDeLaCaja <= 0)
    return { fx: 1, fy: 1 };
  return aspecto > aspectoDeLaCaja
    ? { fx: 1, fy: aspectoDeLaCaja / aspecto }
    : { fx: aspecto / aspectoDeLaCaja, fy: 1 };
}

/** El CSS que aplica el encuadre. Va en un wrapper que ocupa el cuadro entero,
 *  con el `<video>`/`<img>` adentro en `inset: 0` — así el transform de las
 *  transiciones (que vive en el medio) no se pisa con este.
 *
 *  `aspecto` es el ancho/alto del contenido, y hace falta para los que NO
 *  llenan su caja. El recorte que elige el usuario es SOBRE LA IMAGEN ("sacale
 *  un 10% por la izquierda"), pero el `clip-path` recorta el WRAPPER, que es el
 *  cuadro entero. Con una imagen centrada y con aire a los costados, los dos
 *  porcentajes no son el mismo: sin convertir, el primer tramo del recorte se
 *  come el aire y la imagen no se mueve — el marco se corre y la imagen queda
 *  intacta, que es exactamente el síntoma que se vio. */
export function estiloDelEncuadre(
  e?: EncuadreDeClip | null,
  aspecto?: number,
  /** Ancho/alto del CUADRO. Sólo hace falta junto con `aspecto`. */
  aspectoDelCuadro?: number,
): EstiloDeEncuadre {
  const n = normalizarEncuadre(e);
  const { crop } = n;
  const { fx, fy } = fraccionesDelContenido(aspecto, aspectoDelCuadro ?? 1);

  // De "% de la imagen" a "% de la caja": primero el aire de ese lado, y
  // después la parte proporcional de lo que ocupa la imagen.
  const aire = (f: number) => ((1 - f) / 2) * 100;
  const enLaCaja = (valor: number, f: number) => aire(f) + valor * f;

  const hayRecorte =
    crop.top > 0 || crop.right > 0 || crop.bottom > 0 || crop.left > 0;
  const hayTransform = n.scale !== 1 || n.xPct !== 50 || n.yPct !== 50 || n.rotation !== 0;

  const partes: string[] = [];
  if (n.xPct !== 50 || n.yPct !== 50) {
    // El translate en % es relativo al tamaño del wrapper, que es el cuadro
    // entero: mover 50 puntos porcentuales = medio cuadro.
    partes.push(`translate(${n.xPct - 50}%, ${n.yPct - 50}%)`);
  }
  if (n.rotation !== 0) partes.push(`rotate(${n.rotation}deg)`);
  if (n.scale !== 1) partes.push(`scale(${n.scale})`);

  return {
    clipPath: hayRecorte
      ? `inset(${enLaCaja(crop.top, fy)}% ${enLaCaja(crop.right, fx)}% ${enLaCaja(crop.bottom, fy)}% ${enLaCaja(crop.left, fx)}%)`
      : undefined,
    transform: hayTransform ? partes.join(" ") : undefined,
    transformOrigin: "center center",
  };
}

export interface TamanoCuadro {
  w: number;
  h: number;
}

export interface CajaDelEncuadre {
  /** Centro de la caja visible, en px dentro del cuadro. */
  centroXPx: number;
  centroYPx: number;
  /** Tamaño de la caja visible ya escalada, en px. */
  anchoPx: number;
  altoPx: number;
  /** Giro a aplicarle al marco de selección para que calce con el clip. */
  rotacionGrados: number;
}

/** Dónde queda, en pixels de pantalla, el rectángulo que el usuario ve del
 *  clip. Es lo que necesita el marco de selección para pararse justo encima.
 *
 *  Se calcula en PIXELS y no en porcentajes porque con la rotación los dos
 *  ejes se mezclan: en un cuadro 9:16 un giro de 45° sobre porcentajes daría
 *  un rectángulo deformado. */
export function cajaDelEncuadre(
  e: EncuadreDeClip | null | undefined,
  cuadro: TamanoCuadro,
  /** Ancho/alto del contenido. Con esto la caja es la del CONTENIDO —lo que se
   *  ve— y no la del cuadro: un video llena la suya y las dos coinciden, una
   *  imagen con `contain` no. El marco se para acá y el ancla del escalado se
   *  mide acá, así que tienen que ser la misma caja o el gesto se siente roto. */
  aspecto?: number,
): CajaDelEncuadre {
  const n = normalizarEncuadre(e);
  const { crop } = n;

  // 1. La caja del encuadre, ya escalada y sin recortar.
  const anchoCaja = cuadro.w * n.scale;
  const altoCaja = cuadro.h * n.scale;

  // 2. Lo que el contenido ocupa de esa caja (`contain` deja aire en un eje).
  const { fx, fy } = fraccionesDelContenido(
    aspecto,
    altoCaja > 0 ? anchoCaja / altoCaja : 1,
  );
  const ancho = anchoCaja * fx;
  const alto = altoCaja * fy;

  // 3. El recorte, que es SOBRE EL CONTENIDO. Achica la caja y, si es
  //    asimétrico, corre su centro: el borde que se arrastró se mueve y el de
  //    enfrente queda donde estaba.
  const anchoVisible = ancho * (1 - (crop.left + crop.right) / 100);
  const altoVisible = alto * (1 - (crop.top + crop.bottom) / 100);
  const corrimientoX = (ancho * (crop.left - crop.right)) / 200;
  const corrimientoY = (alto * (crop.top - crop.bottom)) / 200;

  // 4. El centro: el del cuadro más lo que lo movió el usuario, y encima el
  //    corrimiento del recorte, que gira con el clip.
  const rad = (n.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  return {
    centroXPx:
      cuadro.w / 2 +
      ((n.xPct - 50) / 100) * cuadro.w +
      (corrimientoX * cos - corrimientoY * sin),
    centroYPx:
      cuadro.h / 2 +
      ((n.yPct - 50) / 100) * cuadro.h +
      (corrimientoX * sin + corrimientoY * cos),
    anchoPx: anchoVisible,
    altoPx: altoVisible,
    rotacionGrados: n.rotation,
  };
}

export type ManijaDeEncuadre = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export const ES_ESQUINA = (h: ManijaDeEncuadre): boolean => h.length === 2;

/** Pasa un delta de puntero (pixels de PANTALLA) al sistema de coordenadas del
 *  clip, deshaciendo el giro y la escala. Sin esto, con el clip rotado 90°
 *  arrastrar hacia la derecha recortaría por arriba. */
function deltaLocal(
  dx: number,
  dy: number,
  rotacionGrados: number,
  scale: number,
): { dx: number; dy: number } {
  const rad = (-rotacionGrados * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const s = scale === 0 ? 1 : scale;
  return {
    dx: (dx * cos - dy * sin) / s,
    dy: (dx * sin + dy * cos) / s,
  };
}

/** La esquina de enfrente. Es la que queda CLAVADA mientras se escala. */
const ESQUINA_OPUESTA: Record<string, ManijaDeEncuadre> = {
  nw: "se",
  ne: "sw",
  se: "nw",
  sw: "ne",
};

/** Un vértice de la caja, en px de pantalla, ya girado. */
export function verticeDeLaCaja(
  caja: CajaDelEncuadre,
  esquina: ManijaDeEncuadre,
): { x: number; y: number } {
  const sx = esquina.includes("w") ? -1 : 1;
  const sy = esquina.includes("n") ? -1 : 1;
  const ox = (sx * caja.anchoPx) / 2;
  const oy = (sy * caja.altoPx) / 2;
  const rad = (caja.rotacionGrados * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: caja.centroXPx + ox * cos - oy * sin,
    y: caja.centroYPx + ox * sin + oy * cos,
  };
}

/** Escalar arrastrando una ESQUINA, con la esquina OPUESTA como ancla: esa
 *  queda clavada y el clip crece o se achica hacia ella. Arrastrar la de
 *  arriba a la izquierda achica el clip hacia la de abajo a la derecha.
 *
 *  Siempre es proporcional —una sola `scale`, el video no se deforma— y la
 *  manija sigue al puntero 1:1 a lo largo de la diagonal: el gesto se mide
 *  proyectando el arrastre sobre la recta que une el ancla con la manija, y la
 *  escala cambia en la misma proporción en que cambió esa distancia. (El
 *  cálculo anterior movía el doble, porque escalaba respecto del CENTRO: ahí
 *  correr la manija `d` agrandaba `2d`.)
 *
 *  Como la diagonal se toma de la caja YA girada, esto funciona igual con el
 *  clip rotado, sin tener que deshacer el giro aparte.
 *
 *  Devuelve también la posición, porque mantener el ancla quieta es mover el
 *  centro: escalar y reposicionar son el mismo gesto y salen juntos. */
export function escalarDesdeEsquina(params: {
  encuadreBase: EncuadreDeClip;
  handle: ManijaDeEncuadre;
  dx: number;
  dy: number;
  cuadro: TamanoCuadro;
  /** Ancho/alto del contenido, para los que no llenan su caja (imágenes con
   *  `object-contain`). Sin esto el ancla se calcula sobre la caja del
   *  encuadre y la esquina opuesta se corre en el eje que sobra. */
  aspectoDelContenido?: number;
}): { scale: number; xPct: number; yPct: number } {
  const base = normalizarEncuadre(params.encuadreBase);
  const { handle, cuadro } = params;
  const sinCambio = { scale: base.scale, xPct: base.xPct, yPct: base.yPct };

  if (!ES_ESQUINA(handle) || !cuadro.w || !cuadro.h) return sinCambio;

  const cajaAntes = cajaDelEncuadre(base, cuadro, params.aspectoDelContenido);
  const esquinaAncla = ESQUINA_OPUESTA[handle];
  const ancla = verticeDeLaCaja(cajaAntes, esquinaAncla);
  const manija = verticeDeLaCaja(cajaAntes, handle);

  const vx = manija.x - ancla.x;
  const vy = manija.y - ancla.y;
  const diagonal = Math.hypot(vx, vy);
  if (diagonal < 1e-6) return sinCambio;

  // Cuánto avanzó el puntero EN LA DIRECCIÓN de la diagonal. Lo perpendicular
  // se ignora: mover la manija de costado no puede deformar un clip que se
  // escala proporcionalmente.
  const avance = (params.dx * vx + params.dy * vy) / diagonal;
  const scale = clamp(
    (base.scale * (diagonal + avance)) / diagonal,
    ESCALA_MIN,
    ESCALA_MAX,
  );

  // Con la escala nueva el ancla se corrió (el escalado es respecto del centro
  // del cuadro). Se compensa moviendo el clip lo mismo, en sentido contrario.
  const cajaDespues = cajaDelEncuadre(
    { ...base, scale },
    cuadro,
    params.aspectoDelContenido,
  );
  const anclaDespues = verticeDeLaCaja(cajaDespues, esquinaAncla);

  return {
    scale,
    xPct: base.xPct + ((ancla.x - anclaDespues.x) / cuadro.w) * 100,
    yPct: base.yPct + ((ancla.y - anclaDespues.y) / cuadro.h) * 100,
  };
}

/** Recorte nuevo al arrastrar un LADO. El borde que agarrás sigue al puntero y
 *  el de enfrente queda quieto, así que la caja se achica desde ese lado. */
export function recortarDesdeLado(params: {
  recorteBase: RecorteDeClip;
  handle: ManijaDeEncuadre;
  dx: number;
  dy: number;
  cuadro: TamanoCuadro;
  rotacionGrados?: number;
  scale?: number;
}): RecorteDeClip {
  const { recorteBase, handle, cuadro } = params;
  if (!cuadro.w || !cuadro.h) return recorteBase;

  const local = deltaLocal(
    params.dx,
    params.dy,
    params.rotacionGrados ?? 0,
    params.scale ?? 1,
  );
  const dxPct = (local.dx / cuadro.w) * 100;
  const dyPct = (local.dy / cuadro.h) * 100;

  const r: RecorteDeClip = { ...recorteBase };

  // El tope de cada lado es lo que deja libre el lado opuesto: los dos juntos
  // nunca pueden pasar RECORTE_MAX_POR_EJE.
  if (handle === "w") {
    r.left = clamp(recorteBase.left + dxPct, 0, RECORTE_MAX_POR_EJE - r.right);
  } else if (handle === "e") {
    r.right = clamp(recorteBase.right - dxPct, 0, RECORTE_MAX_POR_EJE - r.left);
  } else if (handle === "n") {
    r.top = clamp(recorteBase.top + dyPct, 0, RECORTE_MAX_POR_EJE - r.bottom);
  } else if (handle === "s") {
    r.bottom = clamp(recorteBase.bottom - dyPct, 0, RECORTE_MAX_POR_EJE - r.top);
  }

  return r;
}

/** Tolerancia del imán a los ángulos rectos, en grados. Sin esto es casi
 *  imposible dejar un video derecho o exactamente de costado a mano. */
export const IMAN_ANGULO_GRADOS = 4;

/** Normaliza a (-180, 180] para que el número guardado no crezca sin control
 *  dando vueltas. */
export function normalizarAngulo(grados: number): number {
  let a = grados % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return a;
}

/** Giro nuevo mientras se arrastra la manija de rotación. Se trabaja con la
 *  DIFERENCIA de ángulo respecto de donde arrancó el gesto (no con el ángulo
 *  absoluto del puntero), así el clip no pega un salto al agarrarlo. */
export function rotarDesdeManija(params: {
  rotacionBase: number;
  /** Centro del clip en coordenadas de pantalla. */
  centro: { x: number; y: number };
  /** Puntero al empezar el gesto y ahora, en coordenadas de pantalla. */
  desde: { x: number; y: number };
  hasta: { x: number; y: number };
  /** Imán a 0/90/180/270. Default true. */
  iman?: boolean;
}): number {
  const { centro, desde, hasta } = params;
  const angDesde = Math.atan2(desde.y - centro.y, desde.x - centro.x);
  const angHasta = Math.atan2(hasta.y - centro.y, hasta.x - centro.x);
  const delta = ((angHasta - angDesde) * 180) / Math.PI;

  let bruto = normalizarAngulo(params.rotacionBase + delta);

  if (params.iman !== false) {
    for (const recto of [-180, -90, 0, 90, 180]) {
      if (Math.abs(bruto - recto) <= IMAN_ANGULO_GRADOS) {
        bruto = recto;
        break;
      }
    }
  }
  return normalizarAngulo(bruto);
}

/** Qué tan cerca (en PIXELES de pantalla) tiene que pasar un borde para que el
 *  imán lo agarre. Se mide en pixels y no en porcentaje del cuadro a propósito:
 *  lo que importa es cuánto le erró la mano al usuario, y eso es una distancia
 *  en pantalla — en porcentaje, el imán sería más fuerte en un cuadro chico que
 *  en uno grande. Mismo criterio que el imán de orillas de la timeline. */
export const IMAN_BORDE_PX = 10;

export interface ResultadoDelIman {
  xPct: number;
  yPct: number;
  /** Dónde dibujar la guía, en px del cuadro. `null` = ese eje no pegó. */
  guiaXPx: number | null;
  guiaYPx: number | null;
}

/** Pega el clip a los bordes del cuadro (y a sus dos ejes centrales) cuando
 *  pasa cerca, mientras se lo arrastra.
 *
 *  Los dos ejes se resuelven por SEPARADO, y de ahí salen gratis las esquinas:
 *  acercarse a la de arriba a la izquierda es pegar el borde izquierdo en X y
 *  el de arriba en Y, cada uno por su lado.
 *
 *  En cada eje gana el destino más cercano, y sólo si está dentro de la
 *  tolerancia. Los destinos son los dos bordes y el centro: centrar es lo que
 *  más se hace y a ojo nunca sale exacto.
 *
 *  Con el clip GIRADO el imán no actúa: un clip rotado no tiene bordes
 *  paralelos a los del cuadro, así que "pegar el borde izquierdo" no quiere
 *  decir nada — habría que decidir entre la esquina que más sobresale, el lado
 *  del bounding box o el borde real del clip, y cualquiera de las tres da
 *  saltos raros mientras se arrastra. */
export function imantarAlCuadro(params: {
  encuadre: EncuadreDeClip;
  cuadro: TamanoCuadro;
  toleranciaPx?: number;
}): ResultadoDelIman {
  const n = normalizarEncuadre(params.encuadre);
  const { cuadro } = params;
  const tol = params.toleranciaPx ?? IMAN_BORDE_PX;
  const sinIman: ResultadoDelIman = {
    xPct: n.xPct,
    yPct: n.yPct,
    guiaXPx: null,
    guiaYPx: null,
  };

  if (n.rotation !== 0 || !cuadro.w || !cuadro.h) return sinIman;

  const caja = cajaDelEncuadre(n, cuadro);

  // Para cada eje: qué punto del clip se compara contra qué punto del cuadro.
  const mejorDe = (
    pares: Array<{ delClip: number; delCuadro: number }>,
  ): { delta: number; guia: number } | null => {
    let mejor: { delta: number; guia: number; dist: number } | null = null;
    for (const par of pares) {
      const dist = Math.abs(par.delClip - par.delCuadro);
      if (dist > tol) continue;
      if (!mejor || dist < mejor.dist) {
        mejor = { delta: par.delCuadro - par.delClip, guia: par.delCuadro, dist };
      }
    }
    return mejor ? { delta: mejor.delta, guia: mejor.guia } : null;
  };

  const ejeX = mejorDe([
    { delClip: caja.centroXPx - caja.anchoPx / 2, delCuadro: 0 },
    { delClip: caja.centroXPx + caja.anchoPx / 2, delCuadro: cuadro.w },
    { delClip: caja.centroXPx, delCuadro: cuadro.w / 2 },
  ]);
  const ejeY = mejorDe([
    { delClip: caja.centroYPx - caja.altoPx / 2, delCuadro: 0 },
    { delClip: caja.centroYPx + caja.altoPx / 2, delCuadro: cuadro.h },
    { delClip: caja.centroYPx, delCuadro: cuadro.h / 2 },
  ]);

  return {
    // El translate es lineal sobre la posición: correr el clip `delta` px es
    // sumarle esa misma fracción del cuadro a xPct/yPct.
    xPct: ejeX ? n.xPct + (ejeX.delta / cuadro.w) * 100 : n.xPct,
    yPct: ejeY ? n.yPct + (ejeY.delta / cuadro.h) * 100 : n.yPct,
    guiaXPx: ejeX ? ejeX.guia : null,
    guiaYPx: ejeY ? ejeY.guia : null,
  };
}
